import React, { createContext, useEffect, useState, useMemo, useCallback } from "react";
import EncryptedStorage from 'react-native-encrypted-storage';
import { useTranslation } from 'react-i18next';
import lz from 'lz-string';

export type FindifyDataContextType = {
    language: string;
    age: string;
    audioGuide: boolean;
    darkMode: boolean;
    showListsGrid: boolean;
    workspace: any;
    visitWorkspaceSelected: boolean;
    visitVersion: 'accessible' | 'standard' | null;
    favouriteWorkspaces: number[];
    isStorageLoading: boolean;
    setAge: (age: string) => void;
    setDarkMode: (darkMode: boolean) => void;
    setAudioGuide: (audioGuide: boolean) => void;
    setLanguage: (language: string) => void;
    setWorkspace: (workspace: any) => void;
    setFavouriteWorkspaces: (favouriteWorkspaces: number[]) => void;
    setVisitWorkspaceSelected: (visitWorkspaceSelected: boolean) => void;
    setVisitVersion: (visitVersion: 'accessible' | 'standard' | null) => void;
    setShowListsGrid: (showListsGrid: boolean) => void;
    availableWorkspaces: any[];
    setAvailableWorkspaces: (availableWorkspaces: any[]) => void;
    availableWorkspacesTypes: any[];
    setAvailableWorkspacesTypes: (availableWorkspacesTypes: any[]) => void;
    userCustomerAccounts: any;
    setUserCustomerAccounts: (userCustomerAccounts: any) => void;
    hasWorkspaceError: boolean;
    setHasWorkspaceError: (hasWorkspaceError: boolean) => void;
}

export const FindifyDataContext = createContext<FindifyDataContextType | null>(null);

export const FindifyDataProvider = ({ children }: { children: React.ReactNode }) => {
    const [languageState, setLanguageState] = useState('pt-PT');
    const [age, setAge] = useState('child');
    const [darkMode, setDarkMode] = useState(false);
    const [audioGuide, setAudioGuide] = useState(false);
    const [availableWorkspaces, setAvailableWorkspaces] = useState<any[]>([]);
    const [userCustomerAccounts, setUserCustomerAccounts] = useState<any>({});
    const [availableWorkspacesTypes, setAvailableWorkspacesTypes] = useState<any[]>([]);
    const [favouriteWorkspaces, setFavouriteWorkspaces] = useState<number[]>([]);
    const [workspace, setWorkspace] = useState<any>(null);
    const [showListsGrid, setShowListsGrid] = useState(false);
    const [visitWorkspaceSelected, setVisitWorkspaceSelected] = useState(false);
    const [visitVersion, setVisitVersion] = useState<'accessible' | 'standard' | null>(null);
    const [hasWorkspaceError, setHasWorkspaceError] = useState(false);

    const [isStorageLoading, setIsStorageLoading] = useState(true);

    const { i18n } = useTranslation();
    const language = languageState?.startsWith('fr') ? 'pt-PT' : languageState;
    const setLanguage = useCallback((nextLanguage: string) => {
        setLanguageState(nextLanguage?.startsWith('fr') ? 'pt-PT' : nextLanguage);
    }, []);

    useEffect(() => {
        i18n.changeLanguage(language);
    }, [language, i18n]);

    const contextDataObj = useMemo(() => ({
        darkMode,
        language,
        age,
        workspace,
        audioGuide,
        favouriteWorkspaces,
        visitWorkspaceSelected,
        visitVersion,
        showListsGrid,
        availableWorkspaces,
        availableWorkspacesTypes,
        userCustomerAccounts,
        isStorageLoading,
        setAge,
        setDarkMode,
        setLanguage,
        setWorkspace,
        setAudioGuide,
        setFavouriteWorkspaces,
        setVisitWorkspaceSelected,
        setVisitVersion,
        setShowListsGrid,
        setAvailableWorkspaces,
        setAvailableWorkspacesTypes,
        hasWorkspaceError,
        setHasWorkspaceError,
        setUserCustomerAccounts
    }), [
        darkMode, language, age, workspace, audioGuide, favouriteWorkspaces,
        visitWorkspaceSelected, visitVersion, showListsGrid, availableWorkspaces,
        availableWorkspacesTypes, userCustomerAccounts, isStorageLoading,
        hasWorkspaceError
    ]);

    const updateStorage = useCallback(async (data: any) => {
        try {
            await EncryptedStorage.setItem('findifyDataLZ', lz.compressToUTF16(JSON.stringify(data)));
        } catch (error) {
            console.error("Failed to update storage:", error);
        }
    }, [setLanguage]);

    const loadStorage = useCallback(async () => {
        try {
            const findifyDataLZ = await EncryptedStorage.getItem('findifyDataLZ');
            if (findifyDataLZ) {
                const findifyData = JSON.parse(lz.decompressFromUTF16(findifyDataLZ));
                if (findifyData) {
                    if (findifyData.showListsGrid !== undefined) setShowListsGrid(findifyData.showListsGrid);
                    if (findifyData.darkMode !== undefined) setDarkMode(findifyData.darkMode);
                    if (findifyData.audioGuide !== undefined) setAudioGuide(findifyData.audioGuide);
                    if (findifyData.language !== undefined) setLanguage(findifyData.language);
                    if (findifyData.age !== undefined) setAge(findifyData.age);
                    if (findifyData.workspace !== undefined) setWorkspace(findifyData.workspace);
                    if (findifyData.favouriteWorkspaces !== undefined) setFavouriteWorkspaces(findifyData.favouriteWorkspaces);
                    if (findifyData.visitVersion !== undefined) setVisitVersion(findifyData.visitVersion);
                    if (findifyData.availableWorkspaces !== undefined) setAvailableWorkspaces(findifyData.availableWorkspaces);
                    if (findifyData.availableWorkspacesTypes !== undefined) setAvailableWorkspacesTypes(findifyData.availableWorkspacesTypes);
                    if (findifyData.userCustomerAccounts !== undefined) setUserCustomerAccounts(findifyData.userCustomerAccounts);
                }
            }
        } catch (error) {
            console.error("Failed to load storage:", error);
        } finally {
            setIsStorageLoading(false);
            setVisitWorkspaceSelected(false);
        }
    }, []);

    useEffect(() => {
        loadStorage();
        // Safety timeout to ensure app boots even if EncryptedStorage hangs on iOS
        const timeout = setTimeout(() => {
            setIsStorageLoading(prev => {
                if (prev) console.warn("Storage loading timed out - forcing app boot");
                return false;
            });
        }, 3000);
        return () => clearTimeout(timeout);
    }, [loadStorage]);

    useEffect(() => {
        if (!isStorageLoading) {
            updateStorage(contextDataObj);
        }
    }, [contextDataObj, isStorageLoading, updateStorage]);

    return (
        <FindifyDataContext.Provider value={contextDataObj}>
            {children}
        </FindifyDataContext.Provider>
    );
};
